import React, { useEffect, useState } from 'react'
import styles from './CreateMenuModal.module.css'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { getMe } from '../../redux/actions/meAction'
import CustomInput from '../UI/CustomInput/CustomInput'
import CustomTextArea from '../UI/CustomTextArea/CustomTextArea'
import { getCompanies } from '../../redux/actions/companiesAction'
import { companiesPerPage, menuPerPage } from '../../constants/constants'
import { useRequestStatus } from '../../hooks/useRequestStatus'
import { Formik, Form, Field, FormikHelpers, FormikValues } from 'formik'
import * as yup from 'yup'
import close from '../../assets/icons/close.svg'
import {
  changeMenuMainInformation,
  createMenu,
  deleteCurrentMenu,
  getAllMenu,
  getCurrentMenu,
  getMenuFromCompany,
} from '../../redux/actions/menuAction'
import IconTrashRed from '../../assets/icons/IconTrashRed.svg'
import CustomButton from '../UI/CustomButton/CustomButton'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import CustomModal from '../UI/CustomModal/CustomModal'
import DeleteComponent from '../DeleteComponent/DeleteComponent'
import { useMediaQuery } from 'react-responsive'
import { useToast } from '../../context/ToastContext'
import { resetCurrentMenu } from '../../redux/reducers/menu/menuSlice'

interface CreateMenuModalProps {
  isOpen: boolean
  onClose: () => void
  currentMenu?: any
  isEditMode: boolean
}

const CreateMenuModal: React.FC<CreateMenuModalProps> = ({
  isOpen,
  onClose,
  currentMenu,
  isEditMode,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [companyId, setCompanyId] = useState<number>(-1)
  const [currentPage, setCurrentPage] = useState(1)
  const [loaded, setLoaded] = useState<boolean>(false)
  const { companies } = useAppSelector((state) => state.companies)
  const { meData } = useAppSelector((state) => state.me)
  const { userRole, token } = useAppSelector((state) => state.auth)
  const debouncedStatus = useRequestStatus('')
  const [visibleDelete, setVisibleDelete] = useState<boolean>(false)
  const { showSuccess, showError } = useToast()

  useEffect(() => {
    if (userRole !== 'superAdmin' && userRole !== 'admin') {
      dispatch(getMe({ token }))
    }
  }, [dispatch, token, userRole])

  useEffect(() => {
    dispatch(
      getCompanies({
        token,
        page: currentPage,
        itemsPerPage: companiesPerPage,
        role: debouncedStatus,
      }),
    )
  }, [dispatch, currentPage, debouncedStatus, token])
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  useEffect(() => {
    if (userRole !== 'superAdmin' && userRole !== 'admin' && meData?.company) {
      setCompanyId(meData.company.id)
    }
  }, [meData, userRole])

  const validationSchema = yup.object().shape({
    menuName: yup.string().required('Название меню обязательно'),
    companyId: yup.number().required('Компания обязательна'),
  })
  const handleSubmitHandler = async (
    values: FormikValues,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<any>,
  ) => {
    const _companyId =
      userRole === 'superAdmin' || userRole === 'admin' ? companyId : meData.company.id

    const requestData = {
      name: values.menuName,
      description: values.description,
      companyId: String(_companyId),
      categoryIds: [],
    }

    setLoaded(true)
    const resultAction = await dispatch(createMenu({ data: requestData, token }))

    if (createMenu.fulfilled.match(resultAction)) {
      showSuccess('Меню успешно создано')
      setLoaded(false)
      resetForm()
      onClose()
      if (userRole === 'directorPlace' || userRole === 'employeePlace') {
        dispatch(getMenuFromCompany({ token, page: 1, itemsPerPage: menuPerPage }))
      } else {
        dispatch(getAllMenu({ token, page: 1, itemsPerPage: menuPerPage }))
      }
    } else {
      const errorMessage = resultAction.error?.message || 'Ошибка при создании меню'
      showError('Ошибка при создании меню')
      setErrors({ menuName: errorMessage })
      setLoaded(false)
    }

    setSubmitting(false)
  }
  if (!isOpen) return null

  const handleSubmitHandlerEdit = async (
    values: FormikValues,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<any>,
  ) => {
    const requestData = {
      name: values.menuName,
      description: values.description,
      menuId: currentMenu && String(currentMenu.id),
    }

    setLoaded(true)
    const resultActionMainInfo = await dispatch(
      changeMenuMainInformation({
        token,
        menuId: requestData.menuId as string,
        name: requestData.name,
        description: requestData.description,
      }),
    )

    if (changeMenuMainInformation.fulfilled.match(resultActionMainInfo)) {
      showSuccess('Информация успешно изменена')
      setLoaded(false)
      resetForm()
      onClose()
      await dispatch(getCurrentMenu({ menuId: requestData.menuId as string, token }))
      if (userRole === 'directorPlace' || userRole === 'employeePlace') {
        dispatch(getMenuFromCompany({ token, page: 1, itemsPerPage: menuPerPage }))
      } else {
        dispatch(getAllMenu({ token, page: 1, itemsPerPage: menuPerPage }))
      }
      onClose()
    } else {
      const errorMessage = resultActionMainInfo.error?.message || 'Ошибка при изменении меню'
      showError('Ошибка при изменении меню')
      setErrors({ menuName: errorMessage })
      setLoaded(false)
    }

    setSubmitting(false)
  }

  const onDeletePlatformHandler = async () => {
    const resultAction = await dispatch(
      deleteCurrentMenu({ menuId: currentMenu && String(currentMenu.id), token }),
    )

    if (deleteCurrentMenu.rejected.match(resultAction)) {
      showError('Ошибка при удалении меню')
    } else {
      showSuccess('Меню успешно удалено')
      onClose()
      navigate('/menu-page/')
      dispatch(resetCurrentMenu())
    }
  }

  return (
    <div className={styles.createMenuModalOverlay}>
      <CustomModal visible={visibleDelete} setVisible={setVisibleDelete}>
        <DeleteComponent
          title='Удаление меню'
          description='Хотите удалить данное меню'
          onCancelHandler={() => setVisibleDelete(false)}
          onDeleteHandler={onDeletePlatformHandler}
        />
      </CustomModal>
      <div className={styles.createMenuModal}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{isEditMode ? 'Изменить меню' : 'Создать меню'}</h1>
          <img onClick={onClose} className={styles.close} src={close} alt='close' />
        </div>

        <Formik
          initialValues={{
            menuName: isEditMode ? currentMenu?.name || '' : '',
            description: isEditMode ? currentMenu?.description || '' : '',
            companyId: companyId,
          }}
          validationSchema={validationSchema}
          onSubmit={isEditMode ? handleSubmitHandlerEdit : handleSubmitHandler}
        >
          {({ touched, errors, setFieldValue, values }) => (
            <Form className={styles.inputBlocks}>
              <div className={styles.wrapperFields}>
                <div className={styles.inputBlock}>
                  <div className={styles.leftSide}>
                    <h4>Название</h4>
                  </div>
                  <div className={styles.rightSide}>
                    <Field
                      as={CustomInput}
                      placeholder='Ввведите'
                      type='text'
                      name='menuName'
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputBlock}>
                  <div className={styles.leftSide}>
                    <h4>Описание</h4>
                  </div>
                  <div className={styles.rightSide}>
                    <Field
                      placeholder='Ввведите'
                      as={CustomTextArea}
                      name='description'
                      withoutFile
                    />
                  </div>
                </div>

                {userRole === 'superAdmin' && (
                  <div className={styles.inputBlock}>
                    <div className={styles.leftSide}>Компания</div>
                    <div className={styles.rightSide}>
                      <Select
                        className={styles.reactSelect}
                        classNamePrefix='react-select'
                        value={
                          values.companyId &&
                          companies.companies.find((c) => c.id === values.companyId)?.name
                            ? {
                                value: values.companyId,
                                label:
                                  companies.companies.find((c) => c.id === values.companyId)
                                    ?.name || '',
                              }
                            : null
                        }
                        options={companies.companies.map((company) => ({
                          value: company.id,
                          label: company.name,
                        }))}
                        onChange={(selectedOption) => {
                          const selectedCompanyId = selectedOption ? selectedOption.value : null
                          setCompanyId(selectedCompanyId ?? 0)
                          setFieldValue('companyId', selectedCompanyId)
                        }}
                        name='companyId'
                        isClearable
                        placeholder='Выберите компанию'
                        noOptionsMessage={() => 'Компания не найдена'}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            border: 'none',
                            boxShadow: 'none',
                            minHeight: 'auto',
                            backgroundColor: 'transparent',
                            height: 23,
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 5,
                            right: 0,
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: 0,
                            padding: 0,
                            height: isMobile ? 40 : 24,
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                        }}
                      />
                      {touched.companyId && errors.companyId && (
                        <div className={styles.error}>{errors.companyId}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.footerContainer}>
                {isEditMode ? (
                  <CustomButton
                    onClick={() => setVisibleDelete(true)}
                    iconSrc={IconTrashRed}
                    className={styles.deleteBtn}
                    title={isMobile ? '' : 'Удалить меню'}
                  />
                ) : (
                  <div></div>
                )}
                <CustomButton
                  className={styles.addSiteBtn}
                  title={isEditMode ? 'Сохранить' : 'Создать'}
                  type='submit'
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default CreateMenuModal
